import type * as React from 'react'

import type { CellContext, ColumnDef } from '@tanstack/react-table'

import type {
  FieldSchema,
  ListNode,
  ListSchema,
} from '~/client/src/shared/lists/core'

export interface RecordColumnDef<T> {
  readonly header?: string | (() => React.ReactNode)
  readonly size?: number
  readonly minSize?: number
  readonly maxSize?: number
  readonly enableSorting?: boolean
  readonly meta?: {
    readonly align?: 'left' | 'center' | 'right'
    readonly cellClassName?: string
  }
  readonly cell?: (record: T) => React.ReactNode
}

export interface TableFieldDef<T> extends Omit<FieldSchema<T>, 'value'> {
  readonly value?: FieldSchema<T>['value']
  readonly column?: RecordColumnDef<T>
  readonly configurable?: boolean
  readonly defaultHidden?: boolean
  readonly fixed?: boolean
}

export interface BuildTableFieldsArgs<T> {
  readonly getId: (r: T) => string
  readonly fields: readonly TableFieldDef<T>[]
  readonly bandLevels?: ListSchema<T>['bandLevels']
  readonly emptyOptionLabel?: string
}

export interface TableFields<T> {
  readonly schema: ListSchema<T>
  readonly columns: ColumnDef<ListNode<T>, unknown>[]
  readonly columnConfig: Array<{
    readonly dataKey: string
    readonly label: string
    readonly isFixed: boolean
    readonly isHidden: boolean
  }>
}

function hasValue<T>(
  field: TableFieldDef<T>,
): field is TableFieldDef<T> & { readonly value: FieldSchema<T>['value'] } {
  return typeof field.value === 'function'
}

export function buildTableFields<T>({
  getId,
  fields,
  bandLevels,
  emptyOptionLabel,
}: BuildTableFieldsArgs<T>): TableFields<T> {
  return {
    schema: {
      getId,
      fields: fields.flatMap(field => {
        if (!hasValue(field)) return []

        const { column: _column, ...schemaField } = field
        return [schemaField]
      }),
      bandLevels,
      emptyOptionLabel,
    },
    columns: fields.flatMap(field => {
      if (!field.column) return []

      const { cell, ...columnDef } = field.column
      const column: ColumnDef<ListNode<T>, unknown> = {
        id: field.key,
        header: field.label,
        ...(hasValue(field)
          ? {
              accessorFn: (node: ListNode<T>) =>
                node.kind === 'record' ? field.value(node.record) : undefined,
            }
          : {}),
        ...columnDef,
        ...(cell
          ? {
              cell: (ctx: CellContext<ListNode<T>, unknown>) => {
                const node = ctx.row.original
                if (node.kind === 'band') return null

                return cell(node.record)
              },
            }
          : {}),
      }

      return [column]
    }),
    columnConfig: fields.flatMap(field => {
      if (!field.column || field.configurable === false) return []
      return [
        {
          dataKey: field.key,
          label: field.label,
          isFixed: field.fixed ?? false,
          isHidden: field.defaultHidden ?? false,
        },
      ]
    }),
  }
}

export function buildRecordColumn<T>(
  id: string,
  column: RecordColumnDef<T>,
): ColumnDef<ListNode<T>, unknown> {
  const { cell, ...columnDef } = column
  return {
    id,
    accessorFn: node => (node.kind === 'record' ? node.record : undefined),
    ...columnDef,
    ...(cell
      ? {
          cell: (ctx: CellContext<ListNode<T>, unknown>) =>
            ctx.row.original.kind === 'record'
              ? cell(ctx.row.original.record)
              : null,
        }
      : {}),
  }
}
